<template>
  <v-dialog :model-value="modelValue" width="90%" @keydown.esc="cancel" @click:outside="closeModal">
    <BaseDialogCard :title="tkey('modalTitle')" @close="closeModal">
      <div class="h-100 flex-grow-1 overflow-hidden">
        <AgGridVue
          style="height: 400px"
          class="w-100"
          :column-defs="columnDefs"
          :row-data="rowData"
          :grid-options="gridOptions"
          @grid-ready="onGridReady"
          @selection-changed="onSelectionChanged"
        />
      </div>
      <template #footer>
        <v-card-actions>
          <v-btn primary @click="closeModal">
            {{ $t('actions.cancel') }}
          </v-btn>
          <v-btn
            secondary
            variant="flat"
            class="outlined-btn"
            :disabled="!selectedRows.length"
            :loading="isCreatingWorkpackage"
            @click="createWorkpackageFromTemplate"
          >
            {{ $t('general.select') }}
          </v-btn>
        </v-card-actions>
      </template>
    </BaseDialogCard>
  </v-dialog>
</template>

<script>
import { get, isString } from 'lodash';
import { mapState, mapActions } from 'pinia';
import agGridUtils from '@/utils/ag-grid-utils';
import { useFeature } from '@/composables/use-feature';
import { useInternationalization } from '@/composables/use-internationalization';
import { useDayjs } from '@/composables/use-dayjs';
import { useContextStore } from '@/stores/context';
import { useBundlesStore } from '@/stores/bundles';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useDomUtility } from '@/composables/dom-utils';

export default {
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close'],

  setup() {
    const bundlesStore = useBundlesStore();
    const { bundlesEnabled } = useFeature();
    const { tkey } = useInternationalization('templatesPage.createWpFromTemplateModal');
    const { getUTCFromDate } = useDayjs();
    const { scrollToElement } = useDomUtility();

    return {
      bundlesStore,
      bundlesEnabled,
      tkey,
      getUTCFromDate,
      scrollToElement,
    };
  },
  data() {
    return {
      rowData: [],
      selectedRows: [],
      gridApi: null,
      isCreatingWorkpackage: false,
      gridOptions: {
        columnMenu: 'legacy',
        rowSelection: {
          mode: 'singleRow',
          enableClickSelection: true,
        },
        selectionColumnDef: {
          headerName: this.tkey('gridHeaders.select'),
          suppressNavigable: true,
          maxWidth: 50,
        },
        suppressContextMenu: true,
        headerHeight: 40,
        rowHeight: 40,
        stopEditingWhenCellsLoseFocus: true,
        cellSelection: { handle: { mode: 'fill' } },
        tooltipShowDelay: 0,
        defaultColDef: {
          filter: true,
          resizable: true,
          minWidth: 80,
          sortable: true,
          comparator: agGridUtils.sortings.naturalSort,
          editable: false,
          suppressMovable: true,
        },
      },
    };
  },

  computed: {
    ...mapState(useContextStore, ['getDateFormats']),
    ...mapState(useWorkpackagesStore, ['loadingWorkpackageTemplates', 'workpackageTemplates']),

    loading() {
      return this.loadingWorkpackageTemplates;
    },

    columnDefs() {
      return [
        {
          headerName: this.tkey('gridHeaders.templateTitle'),
          field: 'name',
          colId: 'name',
          flex: 1,
        },
        {
          headerName: this.tkey('gridHeaders.description'),
          field: 'description',
          colId: 'description',
          flex: 2,
        },
        {
          headerName: this.tkey('gridHeaders.bundle'),
          field: 'bundleName',
          colId: 'bundleName',
          hide: !this.bundlesEnabled,
          valueGetter: node => {
            if (!node.data.bundleId) {
              return null;
            }

            return get(this.bundlesStore.bundlesById[node.data.bundleId], 'name');
          },
        },
        {
          headerName: this.tkey('gridHeaders.goLiveDate'),
          field: 'targetLaunchDate',
          colId: 'targetLaunchDate',
          filter: 'agDateColumnFilter',
          hide: !this.bundlesEnabled,
          maxWidth: 100,
          valueGetter: node => {
            if (!node.data.bundleId) {
              return null;
            }

            const bundle = this.bundlesStore.bundlesById[node.data.bundleId];
            const targetLaunchDate = get(bundle, 'targetLaunchDate');
            return targetLaunchDate && isString(targetLaunchDate)
              ? this.getUTCFromDate(targetLaunchDate)
              : '-';
          },
          valueFormatter: params => {
            if (!params.data.bundleId) {
              return '-';
            }
            return agGridUtils.formatters.datesFormatter(params, this.getDateFormats.long);
          },
        },
        {
          headerName: this.tkey('gridHeaders.numberOfAssortmentGroups'),
          field: 'assortmentGroups',
          colId: 'assortmentGroups',
          valueGetter: params => get(params, 'data.assortmentGroups', []).length,
        },
        {
          headerName: this.tkey('gridHeaders.stores'),
          field: 'storeCount.total',
          colId: 'storeCount',
          maxWidth: 100,
          valueGetter: params => get(params, 'data.storeCount.total', 0),
        },
        {
          headerName: this.tkey('gridHeaders.products'),
          // display count of products that are included on template level
          field: 'productCount.includedFromTemplate',
          colId: 'productCount',
          maxWidth: 100,
          valueGetter: params => get(params, 'data.productCount.includedFromTemplate', 0),
        },

        {
          headerName: this.tkey('gridHeaders.creator'),
          field: 'createdBy',
          colId: 'createdBy',
          filter: 'agSetColumnFilter',
          filterParams: { valueFormatter: ({ value }) => value || '-' },
          valueGetter: params => get(params, 'data.createdBy.fullName', '-'),
        },
      ];
    },
  },

  async created() {
    await this.bundlesStore.fetchBundles();
    await this.fetchWorkpackages({ shouldGetTemplates: true });
    this.rowData = this.workpackageTemplates;
    this.selectedRows = [];
  },

  methods: {
    ...mapActions(useWorkpackagesStore, ['fetchWorkpackages', 'addWorkpackage']),

    async createWorkpackageFromTemplate() {
      this.isCreatingWorkpackage = true;

      const bundleId = get(this.bundlesStore.selectedBundle, '_id');
      const template = this.selectedRows[0];
      const addedWorkpackage = await this.addWorkpackage({
        templateId: template._id,
        bundleId: bundleId || template.bundleId,
      });
      await this.fetchWorkpackages({ bundleId });
      this.isCreatingWorkpackage = false;
      this.scrollToElement(`wp-list-element-${addedWorkpackage._id}`);
      this.closeModal();
    },

    cancel() {
      this.$emit('cancel');
    },

    onGridReady(params) {
      this.gridApi = params.api;
    },

    onSelectionChanged() {
      this.selectedRows = this.gridApi.getSelectedRows();
    },

    closeModal() {
      if (this.gridApi) this.gridApi.deselectAll();
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
p {
  font-size: 1.3rem;
  padding-top: 20px;
}

:deep(.dialog-card__footer) {
  justify-content: flex-end;

  .v-card__actions {
    padding: 0;
  }
}
</style>
